import Phaser from 'phaser';

const GOLD = 0xd4af37;

/**
 * Full-canvas dim layer with polygon-shaped erase cutouts. The cutouts
 * expose the felt below; a separate gold ring strokes each polygon and
 * pulses gently to draw the eye.
 *
 * Used by the tutorial (per-step highlights) and by help mode (full-canvas
 * blue tint, no cutouts).
 */
export class HighlightOverlay {
  private readonly scene: Phaser.Scene;
  private readonly rt: Phaser.GameObjects.RenderTexture;
  private readonly ringGfx: Phaser.GameObjects.Graphics;
  private dimAlpha = 0.6;
  private dimColor = 0x000000;
  private polygons: number[][] = [];
  private safeBottomY: number | undefined;
  private safeTopY: number | undefined;
  private ringTween: Phaser.Tweens.Tween | undefined;

  constructor(scene: Phaser.Scene, depth: number) {
    this.scene = scene;
    const W = scene.scale.width;
    const H = scene.scale.height;
    this.rt = scene.add.renderTexture(0, 0, W, H).setOrigin(0, 0).setDepth(depth);
    this.ringGfx = scene.add.graphics().setDepth(depth + 1);
    this.rt.setVisible(false);
    this.ringGfx.setVisible(false);
  }

  isVisible(): boolean {
    return this.rt.visible;
  }

  setVisible(v: boolean) {
    this.rt.setVisible(v);
    this.ringGfx.setVisible(v);
  }

  setDimAlpha(alpha: number) {
    this.dimAlpha = alpha;
    this.redraw();
  }

  setDimColor(color: number) {
    this.dimColor = color;
    this.redraw();
  }

  /** Clamp the dim fill so the bottom strip (e.g. narration card) stays clear. */
  setSafeBottomY(y: number | undefined) {
    this.safeBottomY = y;
    this.safeTopY = undefined;
    this.redraw();
  }

  /** Clamp the dim fill so the top strip stays clear. */
  setSafeTopY(y: number | undefined) {
    this.safeTopY = y;
    this.safeBottomY = undefined;
    this.redraw();
  }

  setHighlights(polygons: number[][]) {
    this.polygons = polygons;
    this.redraw();
  }

  destroy() {
    this.ringTween?.stop();
    this.ringTween = undefined;
    this.rt.destroy();
    this.ringGfx.destroy();
  }

  private redraw() {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    // Hide when there are no polygons to highlight, regardless of dimAlpha.
    // (Plan bug fix: original guard required dimAlpha===0 too, which left the
    //  overlay visible after setHighlights([]) when dimAlpha was non-zero.)
    if (this.polygons.length === 0) {
      this.rt.setVisible(false);
      this.ringGfx.setVisible(false);
      this.ringTween?.stop();
      this.ringTween = undefined;
      return;
    }
    // Build Point[] arrays once per polygon — both the erase pass and the
    // ring stroke below consume the same points.
    const polyPoints = this.polygons.map(flatToPoints);

    this.rt.clear();
    const fillY = this.safeTopY ?? 0;
    const fillH = this.safeBottomY !== undefined ? this.safeBottomY - fillY : H - fillY;
    // Phaser RenderTexture#fill takes (color, alpha, x, y, width, height).
    this.rt.fill(this.dimColor, this.dimAlpha, 0, fillY, W, fillH);

    for (const pts of polyPoints) {
      const erase = this.scene.add.graphics();
      erase.fillStyle(0xffffff, 1);
      erase.fillPoints(pts, true);
      this.rt.erase(erase, 0, 0);
      erase.destroy();
    }
    this.rt.setVisible(true);

    // Past the early-exit guard, polygons.length is guaranteed > 0, so the
    // ring is always drawn here.
    const sm = Math.min(W / 1280, H / 800);
    const lineW = Math.max(2, Math.round(3 * Math.max(0.5, sm)));
    this.ringGfx.clear();
    this.ringGfx.lineStyle(lineW, GOLD, 1);
    for (const pts of polyPoints) this.ringGfx.strokePoints(pts, true);
    this.ringGfx.setVisible(true);
    if (!this.ringTween) {
      this.ringTween = this.scene.tweens.add({
        targets: this.ringGfx,
        alpha: { from: 1.0, to: 0.55 },
        duration: 900,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }
}

function flatToPoints(flat: number[]): Phaser.Geom.Point[] {
  const pts: Phaser.Geom.Point[] = [];
  for (let i = 0; i < flat.length; i += 2)
    pts.push(new Phaser.Geom.Point(flat[i] ?? 0, flat[i + 1] ?? 0));
  return pts;
}
